import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";

const MAX_FILE_BYTES = 15 * 1024 * 1024; // 15MB

export default function AzureExtraction() {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    document.title = "Azure Extraction | Everest";
  }, []);

  const prettyFields = useMemo(() => {
    if (!result?.fields) return null;
    return result.fields as Record<string, any>;
  }, [result]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
  };

  const uploadToStorage = async (file: File) => {
    const bucket = "invoice-pdfs";
    const path = `azure-extraction/${Date.now()}_${file.name}`;
    const { error } = await supabase.storage.from(bucket).upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type || "application/pdf",
    });
    if (error) throw error;
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  };

  const onSubmit = async () => {
    try {
      if (!file) {
        toast({ title: "Select a PDF", description: "Please choose a PDF to process." });
        return;
      }
      if (!file.type.includes("pdf")) {
        toast({ title: "Invalid file", description: "Only PDF files are supported." });
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        toast({ title: "File too large", description: "Max size is 15 MB." });
        return;
      }
      setLoading(true);
      setResult(null);

      const publicUrl = await uploadToStorage(file);

      const { data, error } = await supabase.functions.invoke("azure-extraction", {
        body: { fileUrl: publicUrl },
      });

      if (error) throw error;
      setResult(data);
      toast({ title: "Extraction complete", description: "Azure analysis succeeded." });
    } catch (e: any) {
      console.error(e);
      toast({ title: "Extraction failed", description: e.message || "Unknown error" });
    } finally {
      setLoading(false);
    }
  };

  const labelize = (key: string) =>
    key
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const renderValue = (val: any): JSX.Element => {
    if (val === null || val === undefined) return <span className="text-muted-foreground">—</span>;

    if (Array.isArray(val)) {
      // If array of objects with consistent keys, render a compact, readable table
      const allObjects = val.every((v) => v && typeof v === "object" && !Array.isArray(v));
      if (allObjects && val.length) {
        const cols = Array.from(
          val.reduce<Set<string>>((s, row) => {
            Object.keys(row).forEach((k) => s.add(k));
            return s;
          }, new Set())
        );
        return (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  {cols.map((c) => (
                    <th key={c} className="px-3 py-2 text-left font-medium text-muted-foreground">{labelize(c)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {val.map((row, i) => (
                  <tr key={i} className="border-t even:bg-muted/30">
                    {cols.map((c) => (
                      <td key={c} className="px-3 py-2 align-top">{renderValue(row[c])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      // Fallback: simple list
      return (
        <ul className="list-disc pl-5 space-y-1">
          {val.map((v, i) => (
            <li key={i}>{renderValue(v)}</li>
          ))}
        </ul>
      );
    }

    if (typeof val === "object") {
      return (
        <dl className="divide-y divide-border">
          {Object.entries(val).map(([k, v]) => (
            <div key={k} className="grid grid-cols-[160px_1fr] gap-x-3 gap-y-1 py-1 items-start">
              <dt className="text-sm text-muted-foreground">{labelize(k)}</dt>
              <dd className="text-sm">{renderValue(v)}</dd>
            </div>
          ))}
        </dl>
      );
    }

    return <span>{String(val)}</span>;
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Azure Extraction</h1>
        <p className="text-muted-foreground">Upload a PDF and extract structured fields using your custom Azure Document Intelligence model.</p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Upload PDF</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Input type="file" accept="application/pdf" onChange={handleFileChange} />
          <div>
            <Button onClick={onSubmit} disabled={loading}>
              {loading ? "Processing..." : "Process with Azure"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardHeader>
            <CardTitle>Results</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="pretty">
              <TabsList>
                <TabsTrigger value="pretty">Pretty</TabsTrigger>
                <TabsTrigger value="raw">Raw JSON</TabsTrigger>
              </TabsList>
              <TabsContent value="pretty" className="space-y-4">
                <section aria-labelledby="fields-heading">
                  <h2 id="fields-heading" className="sr-only">Extracted fields</h2>
                  <dl className="divide-y divide-border">
                    <div className="grid grid-cols-[160px_1fr] gap-x-3 gap-y-1 py-1 items-start">
                      <dt className="text-sm text-muted-foreground">docType</dt>
                      <dd className="text-sm">{result.docType || <span className="text-muted-foreground">—</span>}</dd>
                    </div>
                    {prettyFields && (
                      <>
                        {Object.entries(prettyFields).map(([k, v]) => (
                          <div key={k} className="grid grid-cols-[160px_1fr] gap-x-3 gap-y-1 py-1 items-start">
                            <dt className="text-sm text-muted-foreground flex items-center gap-2">
                              <span>{labelize(k)}</span>
                              {typeof result?.confidence?.[k] === "number" && (
                                <span
                                  className="text-xs text-muted-foreground"
                                  title={`confidence ${Math.round(result.confidence[k] * 100)}%`}
                                >
                                  {Math.round(result.confidence[k] * 100)}%
                                </span>
                              )}
                            </dt>
                            <dd className="text-sm">{renderValue(v)}</dd>
                          </div>
                        ))}
                      </>
                    )}
                  </dl>
                </section>
              </TabsContent>
              <TabsContent value="raw">
                <pre className="whitespace-pre-wrap text-sm bg-muted p-4 rounded-md overflow-x-auto">
                  {JSON.stringify(result.raw ?? result, null, 2)}
                </pre>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
